import { Injectable, OnDestroy, inject } from '@angular/core';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/seguranca/auth.service';
import { DashboardNotificacoesService } from 'src/app/dashboard/dashboard-home/service/dashboard-notificacoes.service';
import { Logger } from 'src/app/core/service/logger.service';
import { LABEL_TIPO_RESTRICAO, TipoRestricao } from 'src/app/shared/models/restricao.model';

const log = new Logger('LiberacaoSseService');

@Injectable({ providedIn: 'root' })
export class LiberacaoSseService implements OnDestroy {
  private readonly auth = inject(AuthService);
  private readonly notificacoes = inject(DashboardNotificacoesService);

  private abortController?: AbortController;
  private retryCount = 0;
  private readonly MAX_RETRY_DELAY_MS = 30_000;

  iniciar(): void {
    if (!this.auth.isAdmin() || this.abortController) return;

    const token = this.auth.getAccessToken();
    if (!token) return;

    this.abortController = new AbortController();
    const url = `${environment.apiUrl}/gerencial/notificacoes/admin/stream`;

    fetchEventSource(url, {
      headers: { Authorization: `Bearer ${token}` },
      signal: this.abortController.signal,
      openWhenHidden: true,
      onopen: async (response) => {
        if (!response.ok) throw new Error(`SSE HTTP ${response.status}`);
        this.retryCount = 0;
      },
      onmessage: (ev) => {
        if (ev.event !== 'nova-solicitacao') return;
        try {
          const data = JSON.parse(ev.data) as {
            solicitacaoId: string;
            tipoRestricao: TipoRestricao;
            solicitanteNome: string;
          };
          this.notificacoes.adicionarRestricaoPendente(
            data.solicitacaoId,
            data.tipoRestricao,
            data.solicitanteNome,
          );
          this.notificarBrowser(data.tipoRestricao, data.solicitanteNome);
        } catch {
          log.warn('Payload SSE inválido:', ev.data);
        }
      },
      onerror: (err) => {
        log.warn('Erro na conexão SSE, reconectando...', err);
        return Math.min(1000 * Math.pow(2, this.retryCount++), this.MAX_RETRY_DELAY_MS);
      },
    });
  }

  encerrar(): void {
    this.abortController?.abort();
    this.abortController = undefined;
  }

  private notificarBrowser(tipo: TipoRestricao, solicitante: string): void {
    if (!('Notification' in globalThis)) return;

    const titulo = LABEL_TIPO_RESTRICAO[tipo] ?? 'Restrição';
    const corpo = `Solicitação de ${solicitante || 'usuário'}`;

    if (Notification.permission === 'granted') {
      new Notification(`Liberação: ${titulo}`, { body: corpo, icon: '/favicon.ico' });
      return;
    }

    if (Notification.permission !== 'denied') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          new Notification(`Liberação: ${titulo}`, { body: corpo, icon: '/favicon.ico' });
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.encerrar();
  }
}
